import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

// Click-to-place-a-pin location picker for the Case Timeline Builder. Modeled
// on CameraMapModal.js's lifecycle boilerplate, but interactive: click the
// map to drop/move a pin, then confirm. No address search — this tool is
// local-only and must not call utils/geocode.js's Nominatim-backed functions
// (that file is reserved for the two cloud-backed tools; see CLAUDE.md).

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 13 };

export default function LocationPickerMap({ open, onClose, onConfirm, initialLat, initialLng }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [pin, setPin] = useState(null);

  // Reset the working pin from the caller's current value each time the picker opens.
  useEffect(() => {
    if (open) setPin(initialLat != null && initialLng != null ? { lat: initialLat, lng: initialLng } : null);
  }, [open, initialLat, initialLng]);

  // Init map once per open — centers from the initial props directly (not
  // the `pin` state) so it isn't affected by effect-ordering/timing.
  useEffect(() => {
    if (!open || !containerRef.current || mapRef.current) return;
    const hasInitial = initialLat != null && initialLng != null;
    const start = hasInitial ? { lat: initialLat, lng: initialLng } : DEFAULT_CENTER;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    map.setView([start.lat, start.lng], hasInitial ? 16 : DEFAULT_CENTER.zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    map.on('click', e => setPin({ lat: e.latlng.lat, lng: e.latlng.lng }));
    mapRef.current = map;
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(false); }, 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the on-map marker in sync with the working pin.
  useEffect(() => {
    if (!mapRef.current) return;
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    if (pin) {
      markerRef.current = L.circleMarker([pin.lat, pin.lng], {
        radius: 9, color: '#fff', weight: 3, fillColor: '#6d28d9', fillOpacity: 1,
      }).addTo(mapRef.current);
    }
  }, [pin]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Box
      onClick={onClose}
      // zIndex above MUI's default Dialog/modal layer (1300) — this picker is
      // opened from inside EventEditorDialog's own MUI <Dialog>, and since
      // that Dialog portals to document.body after this component in DOM
      // order, an equal z-index would let the Dialog's backdrop intercept
      // clicks meant for the map underneath it.
      sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{ bgcolor: '#fff', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Pick a location</Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '2px' }}>Click the map to drop a pin.</Typography>
            {pin && (
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '2px', fontFamily: "'Courier New', monospace" }}>
                {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
              </Typography>
            )}
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '22px', lineHeight: 1, p: '2px 6px', '&:hover': { color: '#dc2626' } }}>
            &times;
          </Box>
        </Box>

        <Box ref={containerRef} sx={{ height: 380, width: '100%', zIndex: 0 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '14px 18px', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setPin(null)} variant="text" disabled={!pin} sx={{ color: '#64748b' }}>Clear</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} variant="text" sx={{ color: '#64748b' }}>Cancel</Button>
          <Button
            onClick={() => { onConfirm(pin ? pin.lat : null, pin ? pin.lng : null); onClose(); }}
            variant="contained" disabled={!pin}
            sx={{ bgcolor: '#6d28d9', fontWeight: 600, '&:hover': { bgcolor: '#5b21b6' }, '&:disabled': { bgcolor: '#c4b5fd' } }}
          >
            Use this location
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
